<template>
    <div class="admin-pending-page">
            <header class="admin-pending-header">
                <div>
                    <span class="admin-eyebrow">{{ currentLanguage === 'TA' ? 'நிர்வாகம்' : 'Admin' }}</span>
                    <h1>{{ currentLanguage === 'TA' ? 'ஒப்புதலுக்காக காத்திருக்கும் காணொளிகள்' : 'Pending Video Approvals' }}</h1>
                    <p>{{ currentLanguage === 'TA' ? 'பயனர் சமர்ப்பித்த அல்லது மதிப்பாய்வு தேவைப்படும் காணொளிகளை ஒப்புதல் அளிக்கவும் அல்லது நிராகரிக்கவும்.' : 'Review videos waiting for approval, then publish or reject them.' }}</p>
                </div>
                <button class="refresh-button" :disabled="loading" @click="loadPendingVideos">
                    <vue-feather type="refresh-cw" size="16" :animation="loading ? 'spin' : null"></vue-feather>
                    {{ currentLanguage === 'TA' ? 'புதுப்பி' : 'Refresh' }}
                </button>
            </header>

            <div v-if="loading" class="admin-state">
                <vue-feather type="loader" animation="spin" size="32"></vue-feather>
                <span>{{ currentLanguage === 'TA' ? 'காணொளிகளை ஏற்றுகிறது...' : 'Loading pending videos...' }}</span>
            </div>

            <div v-else-if="errorMessage" class="admin-alert error">
                <vue-feather type="alert-circle" size="18"></vue-feather>
                <span>{{ errorMessage }}</span>
            </div>

            <div v-else-if="pendingVideos.length === 0" class="admin-state empty">
                <vue-feather type="check-circle" size="40" stroke-width="1.8"></vue-feather>
                <h2>{{ currentLanguage === 'TA' ? 'நிலுவையில் காணொளிகள் இல்லை' : 'No videos pending approval' }}</h2>
                <p>{{ currentLanguage === 'TA' ? 'மதிப்பாய்வு செய்ய வேண்டிய புதிய காணொளிகள் வந்தால் இங்கே காட்டப்படும்.' : 'New videos that need review will appear here.' }}</p>
            </div>

            <div v-else class="pending-video-list">
                <article v-for="video in pendingVideos" :key="video.id" class="pending-video-row">
                    <router-link class="pending-thumb" :to="`/watch/${video.id}`">
                        <img :src="thumbnailFor(video)" :alt="displayTitle(video)" @error="handleThumbError" />
                        <span class="duration-badge">{{ formattedDuration(video.duration) }}</span>
                    </router-link>

                    <div class="pending-video-details">
                        <div class="pending-title-line">
                            <router-link class="pending-title" :to="`/watch/${video.id}`">
                                {{ displayTitle(video) }}
                            </router-link>
                            <span :class="['status-chip', video.publishState || 'unknown']">{{ video.publishState || 'unknown' }}</span>
                            <span :class="['status-chip', video.status || 'unknown']">{{ video.status || 'unknown' }}</span>
                        </div>
                        <div class="pending-meta">
                            <span>ID: {{ video.id }}</span>
                            <span>{{ formatBytes(video.size) }}</span>
                            <span>{{ timeAgo(Date.parse(video.updatedAt || video.createdAt)) }}</span>
                        </div>
                        <p v-if="video.descriptionEN || video.descriptionTA" class="pending-description">
                            {{ video.descriptionEN || video.descriptionTA }}
                        </p>
                        <p v-if="video.errorMessage" class="pending-error">{{ video.errorMessage }}</p>
                    </div>

                    <div class="pending-actions">
                        <button
                            class="approve-button"
                            :disabled="isUpdating(video.id)"
                            @click="approveVideo(video)"
                        >
                            <vue-feather v-if="isUpdating(video.id, 'approve')" type="loader" animation="spin" size="15"></vue-feather>
                            <vue-feather v-else type="check" size="15"></vue-feather>
                            {{ currentLanguage === 'TA' ? 'ஒப்புதல்' : 'Approve' }}
                        </button>
                        <button
                            class="reject-button"
                            :disabled="isUpdating(video.id)"
                            @click="rejectVideo(video)"
                        >
                            <vue-feather v-if="isUpdating(video.id, 'reject')" type="loader" animation="spin" size="15"></vue-feather>
                            <vue-feather v-else type="x" size="15"></vue-feather>
                            {{ currentLanguage === 'TA' ? 'நிராகரி' : 'Reject' }}
                        </button>
                    </div>
                </article>

                <button v-if="nextToken" class="load-more-button" :disabled="loadingMore" @click="loadMore">
                    <vue-feather v-if="loadingMore" type="loader" animation="spin" size="16"></vue-feather>
                    {{ loadingMore ? (currentLanguage === 'TA' ? 'ஏற்றுகிறது...' : 'Loading...') : (currentLanguage === 'TA' ? 'மேலும் ஏற்று' : 'Load more') }}
                </button>
            </div>
    </div>
</template>

<script>
import { createSessionPagination } from '../utils/sessionPagination';
import { API } from 'aws-amplify';
import { videosByPublishState } from '../graphql/queries';
import { updateVideoPublishState } from '../graphql/custom';

export default {
    name: 'AdminPendingVideos',
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' }
    },
    data() {
        return {
            loading: false,
            loadingMore: false,
            pendingVideos: [],
            pageProgress: null,
            nextToken: null,
            errorMessage: '',
            updating: {},
        };
    },
    computed: {
        currentLanguage() {
            return this.languageRef || 'EN';
        },
    },
    mounted() {
        this.loadPendingVideos();
    },
    methods: {
        async fetchPendingPage(nextToken = null) {
            try {
                return await API.graphql({
                    query: videosByPublishState,
                    variables: {
                        publishState: 'pending',
                        sortDirection: 'DESC',
                        limit: 50,
                        nextToken,
                    },
                    authMode: 'AMAZON_COGNITO_USER_POOLS',
                });
            } catch (error) {
                // Older deployed schemas use Int for byte sizes. Amplify rejects
                // the response even when only this nullable metadata field fails.
                const items = error.data?.videosByPublishState?.items;
                const onlySizeOverflow = Array.isArray(items) && error.errors?.length > 0 &&
                    error.errors.every(detail => {
                        const path = detail.path;
                        return Array.isArray(path) && path.length === 4 &&
                            path[0] === 'videosByPublishState' && path[1] === 'items' &&
                            Number.isInteger(path[2]) && items[path[2]] && path[3] === 'size' &&
                            detail.message?.includes("Expected type 'Int' but was 'Long'");
                    });
                if (!onlySizeOverflow) throw error;
                for (const detail of error.errors) items[detail.path[2]].size = null;
                return { data: error.data };
            }
        },
        async loadPendingVideos() {
            const progress = createSessionPagination('pending-approvals', this.$parent?.currentUser?.sub || this.$parent?.currentUser?.model?.id || 'admin');
            this.pageProgress = progress;
            this.loadingMore = false;
            this.loading = true;
            this.errorMessage = '';
            try {
                const res = await this.fetchPendingPage();
                if (this.pageProgress !== progress) return;
                const result = res.data.videosByPublishState;
                this.pendingVideos = result.items || [];
                this.nextToken = result.nextToken || null;
                await progress.restore(() => this.pageProgress === progress && this.loadMore());
            } catch (error) {
                if (this.pageProgress !== progress) return;
                console.error('Failed to load pending videos:', error);
                this.errorMessage = this.currentLanguage === 'TA'
                    ? 'நிலுவை காணொளிகளை ஏற்ற முடியவில்லை.'
                    : 'Failed to load pending videos.';
            } finally {
                if (this.pageProgress === progress) this.loading = false;
            }
        },
        async loadMore() {
            if (!this.nextToken || this.loadingMore) return;
            const progress = this.pageProgress;
            this.loadingMore = true;
            this.errorMessage = '';
            try {
                const res = await this.fetchPendingPage(this.nextToken);
                if (this.pageProgress !== progress) return false;
                const result = res.data.videosByPublishState;
                this.pendingVideos = [...this.pendingVideos, ...(result.items || [])];
                this.nextToken = result.nextToken || null;
                progress.loaded();
                return true;
            } catch (error) {
                if (this.pageProgress !== progress) return;
                console.error('Failed to load more pending videos:', error);
                this.errorMessage = this.currentLanguage === 'TA'
                    ? 'மேலும் காணொளிகளை ஏற்ற முடியவில்லை.'
                    : 'Failed to load more videos.';
            } finally {
                if (this.pageProgress === progress) this.loadingMore = false;
            }
        },
        async approveVideo(video) {
            await this.updatePublishState(video, 'published', 'approve');
        },
        async rejectVideo(video) {
            await this.updatePublishState(video, 'rejected', 'reject');
        },
        async updatePublishState(video, publishState, action) {
            this.updating = {
                ...this.updating,
                [video.id]: action,
            };
            this.errorMessage = '';
            try {
                await API.graphql({
                    query: updateVideoPublishState,
                    variables: {
                        input: {
                            id: video.id,
                            publishState,
                        },
                    },
                    authMode: 'AMAZON_COGNITO_USER_POOLS',
                });
                this.pendingVideos = this.pendingVideos.filter(item => item.id !== video.id);
            } catch (error) {
                console.error(`Failed to ${action} video:`, error);
                this.errorMessage = action === 'approve'
                    ? (this.currentLanguage === 'TA' ? 'காணொளிக்கு ஒப்புதல் அளிக்க முடியவில்லை.' : 'Failed to approve video.')
                    : (this.currentLanguage === 'TA' ? 'காணொளியை நிராகரிக்க முடியவில்லை.' : 'Failed to reject video.');
            } finally {
                const nextUpdating = { ...this.updating };
                delete nextUpdating[video.id];
                this.updating = nextUpdating;
            }
        },
        isUpdating(videoId, action = null) {
            if (action) return this.updating[videoId] === action;
            return Boolean(this.updating[videoId]);
        },
        displayTitle(video) {
            return video.titleEN || video.titleTA || `Video ${video.id}`;
        },
        thumbnailFor(video) {
            if (video.thumbnailUrl) {
                return video.thumbnailUrl.startsWith('http')
                    ? video.thumbnailUrl
                    : `https://cdn.eelam.tv/${video.thumbnailUrl}`;
            }
            const imageIndex = video.imageName || '0';
            return `https://cdn1.eelam.tv/public/thumbs/${video.id}_${imageIndex}.jpeg`;
        },
        handleThumbError(event) {
            event.target.style.display = 'none';
            event.target.parentElement?.classList.add('thumb-missing');
        },
        formattedDuration(seconds) {
            const totalSeconds = Number(seconds) || 0;
            const minutes = Math.floor(totalSeconds / 60);
            const remainder = totalSeconds % 60;
            return `${minutes}:${String(remainder).padStart(2, '0')}`;
        },
        formatBytes(bytes) {
            if (bytes == null) return this.currentLanguage === 'TA' ? 'அளவு கிடைக்கவில்லை' : 'Size unavailable';
            const value = Number(bytes) || 0;
            if (!value) return '0 MB';
            const units = ['B', 'KB', 'MB', 'GB'];
            let size = value;
            let unitIndex = 0;
            while (size >= 1024 && unitIndex < units.length - 1) {
                size /= 1024;
                unitIndex += 1;
            }
            return `${size.toFixed(unitIndex <= 1 ? 0 : 1)} ${units[unitIndex]}`;
        },
        timeAgo(timestamp) {
            if (!timestamp) return '';
            const seconds = Math.floor((Date.now() - timestamp) / 1000);
            if (seconds < 60) return 'just now';
            const intervals = [
                { label: 'y', value: 31536000 },
                { label: 'mo', value: 2592000 },
                { label: 'd', value: 86400 },
                { label: 'h', value: 3600 },
                { label: 'm', value: 60 },
            ];
            const interval = intervals.find(item => seconds >= item.value);
            return `${Math.floor(seconds / interval.value)}${interval.label} ago`;
        },
    },
};
</script>

<style scoped>
.admin-pending-page {
    max-width: 1180px;
    margin: 20px auto;
    padding: 0 20px 40px;
}

.admin-pending-header {
    display: flex;
    justify-content: space-between;
    gap: 18px;
    align-items: flex-start;
    margin-bottom: 22px;
}

.admin-eyebrow {
    display: inline-flex;
    margin-bottom: 8px;
    color: #ff1314;
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
}

.admin-pending-header h1 {
    margin: 0;
    color: var(--text-primary);
    font-size: 30px;
    line-height: 1.15;
}

.admin-pending-header p {
    max-width: 650px;
    margin: 10px 0 0;
    color: var(--text-secondary);
    font-size: 15px;
    line-height: 1.5;
}

.refresh-button,
.load-more-button,
.approve-button,
.reject-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 0;
    border-radius: 6px;
    font-weight: 700;
    cursor: pointer;
}

.refresh-button {
    min-height: 40px;
    padding: 0 14px;
    color: var(--text-primary);
    background: var(--bg-card);
    border: 1px solid var(--border-color);
}

.pending-video-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.pending-video-row {
    display: grid;
    grid-template-columns: 190px 1fr auto;
    gap: 18px;
    align-items: center;
    padding: 14px;
    border-radius: 8px;
    background: var(--bg-card);
    box-shadow: 0 2px 8px var(--shadow-color);
}

.pending-thumb {
    position: relative;
    display: block;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: 6px;
    background: var(--bg-tertiary);
}

.pending-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.pending-thumb.thumb-missing::after {
    content: 'No thumbnail';
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-tertiary);
    font-size: 13px;
    font-weight: 700;
}

.duration-badge {
    position: absolute;
    right: 6px;
    bottom: 6px;
    padding: 3px 6px;
    border-radius: 4px;
    color: #fff;
    background: rgba(0, 0, 0, 0.75);
    font-size: 12px;
    font-weight: 800;
}

.pending-video-details {
    min-width: 0;
}

.pending-title-line {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.pending-title {
    min-width: 0;
    overflow: hidden;
    color: var(--text-primary);
    font-size: 18px;
    font-weight: 800;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pending-title:hover {
    color: #ff1314;
}

.status-chip {
    flex: 0 0 auto;
    padding: 3px 8px;
    border-radius: 999px;
    color: #775400;
    background: rgba(255, 193, 7, 0.16);
    font-size: 12px;
    font-weight: 800;
}

.status-chip.ready {
    color: #116329;
    background: rgba(34, 197, 94, 0.12);
}

.status-chip.failed {
    color: #b42318;
    background: rgba(255, 19, 20, 0.1);
}

.pending-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 8px;
    color: var(--text-tertiary);
    font-size: 13px;
}

.pending-description,
.pending-error {
    display: -webkit-box;
    margin: 10px 0 0;
    overflow: hidden;
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.4;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.pending-error {
    color: #b42318;
}

.pending-actions {
    display: flex;
    gap: 8px;
}

.approve-button,
.reject-button {
    min-height: 38px;
    padding: 0 12px;
    color: #fff;
}

.approve-button {
    background: #148f3d;
}

.reject-button {
    background: #c8191e;
}

.refresh-button:disabled,
.load-more-button:disabled,
.approve-button:disabled,
.reject-button:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.load-more-button {
    align-self: center;
    min-height: 42px;
    padding: 0 18px;
    color: var(--text-primary);
    background: var(--bg-card);
    border: 1px solid var(--border-color);
}

.admin-state {
    min-height: 260px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    text-align: center;
    color: var(--text-secondary);
    background: var(--bg-card);
    border-radius: 8px;
    box-shadow: 0 2px 8px var(--shadow-color);
    padding: 32px;
}

.admin-state h1,
.admin-state h2 {
    margin: 0;
    color: var(--text-primary);
}

.admin-state p {
    margin: 0;
    color: var(--text-secondary);
}

.admin-state.denied {
    color: #ff1314;
}

.admin-alert {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 8px;
    font-weight: 700;
}

.admin-alert.error {
    color: #b42318;
    background: rgba(255, 19, 20, 0.1);
}

@media (max-width: 820px) {
    .admin-pending-header {
        flex-direction: column;
    }

    .pending-video-row {
        grid-template-columns: 1fr;
    }

    .pending-actions {
        width: 100%;
    }

    .approve-button,
    .reject-button {
        flex: 1;
    }
}
</style>
